import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./industryExpertise.css";
import { Link } from "react-router-dom";
import { SLUGS, industryPath } from "../../../data/industryRoutes";

import gamingImage from "../../../assets/home/gaming-entertainment-media.webp";
import fashionImage from "../../../assets/home/fashion-retail-ecommerce.webp";
import energyImage from "../../../assets/home/energy-utilities.webp";
import educationImage from "../../../assets/home/education-data-annotation.webp";
import defenceImage from "../../../assets/home/defence-security-surveillance.webp";
import aerialImage from "../../../assets/home/aerial-drone-imaging-surveying.webp";
import agricultureImage from "../../../assets/home/agri-tech-precision-agriculture-livestock.webp";
import adasImage from "../../../assets/home/adas-autonomous-vehicles.webp";
import bankingImage from "../../../assets/home/banking-financial-services-insurance.webp";
import constructionImage from "../../../assets/home/construction-industry.webp";
import geospatialImage from "../../../assets/home/geospatial-satellite-imaging-mapping.webp";
import governmentImage from "../../../assets/home/government-public-sector-smart-cities.webp";
import healthcareImage from "../../../assets/home/healthcare-healthtech-biological.webp";
import hospitalityImage from "../../../assets/home/hospitality-tourism.webp";
import logisticsImage from "../../../assets/home/logistics.webp";
import manufacturingImage from "../../../assets/home/manufacturing.webp";
import marineImage from "../../../assets/home/marine-maritime.webp";
import roboticsImage from "../../../assets/home/robotics1.webp";
import sportsImage from "../../../assets/home/sports-analytics.webp";
import technologyImage from "../../../assets/home/technology-telecommunications.webp";

gsap.registerPlugin(ScrollTrigger);

const industries = [
  "Aerial & Drones: Imaging & Surveying",
  "Agri-Tech: Precision Agriculture & Livestock",
  "ADAS & Autonomous Vehicles",
  "Banking, Financial Services & Insurance",
  "Construction",
  "Defence, Security & Surveillance",
  "Education",
  "Energy & Utilities",
  "Fashion, Retail & E-commerce",
  "Gaming, Entertainment & Media",
  "Geospatial & Satellite: Imaging & Mapping",
  "Government, Public Sector & Smart Cities",
  "Healthcare, Health-Tech & Biological",
  "Hospitality & Tourism",
  "Logistics",
  "Manufacturing",
  "Marine & Maritime",
  "Robotics",
  "Sports Analytics",
  "Technology & Telecommunications",
];

const industryImages = {
  "Aerial & Drones: Imaging & Surveying": aerialImage,
  "Agri-Tech: Precision Agriculture & Livestock": agricultureImage,
  "ADAS & Autonomous Vehicles": adasImage,
  "Banking, Financial Services & Insurance": bankingImage,
  "Construction": constructionImage,
  "Defence, Security & Surveillance": defenceImage,
  "Education": educationImage,
  "Energy & Utilities": energyImage,
  "Fashion, Retail & E-commerce": fashionImage,
  "Gaming, Entertainment & Media": gamingImage,
  "Geospatial & Satellite: Imaging & Mapping": geospatialImage,
  "Government, Public Sector & Smart Cities": governmentImage,
  "Healthcare, Health-Tech & Biological": healthcareImage,
  "Hospitality & Tourism": hospitalityImage,
  "Logistics": logisticsImage,
  "Manufacturing": manufacturingImage,
  "Marine & Maritime": marineImage,
  "Robotics": roboticsImage,
  "Sports Analytics": sportsImage,
  "Technology & Telecommunications": technologyImage,
};

const industryRoutes = {
  "Aerial & Drones: Imaging & Surveying": industryPath(SLUGS.aerialDrones),
  "Agri-Tech: Precision Agriculture & Livestock": industryPath(SLUGS.agriTech),
  "ADAS & Autonomous Vehicles": industryPath(SLUGS.adas),
  "Banking, Financial Services & Insurance": industryPath(SLUGS.bfsi),
  Construction: industryPath(SLUGS.construction),
  "Defence, Security & Surveillance": industryPath(SLUGS.defence),
  Education: industryPath(SLUGS.education),
  "Energy & Utilities": industryPath(SLUGS.energy),
  "Fashion, Retail & E-commerce": industryPath(SLUGS.fashion),
  "Gaming, Entertainment & Media": industryPath(SLUGS.gaming),
  "Geospatial & Satellite: Imaging & Mapping": industryPath(SLUGS.geospatial),
  "Government, Public Sector & Smart Cities": industryPath(SLUGS.government),
  "Healthcare, Health-Tech & Biological": industryPath(SLUGS.healthcare),
  "Hospitality & Tourism": industryPath(SLUGS.hospitality),
  Logistics: industryPath(SLUGS.logistics),
  Manufacturing: industryPath(SLUGS.manufacturing),
  "Marine & Maritime": industryPath(SLUGS.marine),
  Robotics: industryPath(SLUGS.robotics),
  "Sports Analytics": industryPath(SLUGS.sports),
  "Technology & Telecommunications": industryPath(SLUGS.tech),
};

const formatIndex = (index) => String(index + 1).padStart(2, "0");

const AUTOPLAY_MS = 0;

export default function IndustryExpertise() {
  const [view, setView] = useState({ current: 0, previous: null });
  const sectionRef = useRef(null);
  const pausedRef = useRef(false);
  const activeIndex = view.current;
  const previousImage =
    view.previous !== null ? industryImages[industries[view.previous]] : null;
  const activeIndustry = industries[activeIndex];
  const [activeName, activeDetail] = activeIndustry.split(": ");
  const activeImage = industryImages[activeIndustry];
  const activate = (index) => {
    setView((s) => (s.current === index ? s : { current: index, previous: s.current }));
  };

  useEffect(() => {
    if (!AUTOPLAY_MS) return undefined;
    const timer = window.setInterval(() => {
      if (pausedRef.current) return;
      setView((s) => ({ current: (s.current + 1) % industries.length, previous: s.current }));
    }, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const preload = () => {
      Object.values(industryImages).forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    };
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(preload, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(preload, 1500);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const mm = gsap.matchMedia();

    mm.add(
      "(prefers-reduced-motion: no-preference)",
      () => {
        const headingRow = section.querySelector(".industry-heading-row");

        gsap
          .timeline({
            defaults: { ease: "power1.out" },
            scrollTrigger: {
              trigger: headingRow,
              start: "top 70%",
              once: true,
            },
          })
          .from(
            ".industry-title",
            {
              autoAlpha: 0,
              duration: 0.8,
              clearProps: "opacity,visibility",
            },
            0.35,
          )
          .from(
            ".industry-intro",
            {
              autoAlpha: 0,
              duration: 0.8,
              clearProps: "opacity,visibility",
            },
            "-=0.4",
          );
      },
      section,
    );

    document.fonts?.ready.then(() => ScrollTrigger.refresh());

    return () => mm.revert();
  }, []);

  const showPrevious = () => {
    activate((activeIndex - 1 + industries.length) % industries.length);
  };

  const showNext = () => {
    activate((activeIndex + 1) % industries.length);
  };

  return (
    <section
      className="industry-section"
      id="industries"
      ref={sectionRef}
      onMouseEnter={() => {
        pausedRef.current = true;
      }}
      onMouseLeave={() => {
        pausedRef.current = false;
      }}
    >
      <div className="industry-inner">
        <div className="industry-heading-row">
          <div className="industry-heading-content">
            <span className="industry-eyebrow">
              <span className="industry-eyebrow-dot" aria-hidden="true" />
              INDUSTRY EXPERTISE
            </span>

            <h2 className="industry-title">
              <span className="industry-title-line">
                Deep Domain Expertise Across
              </span>
              <br className="industry-break" />
              20+{" "}
              <span className="industry-title-accent">Industry Verticals</span>
            </h2>
          </div>

          <div className="industry-intro">
            <p className="industry-description">
              Backed by deep domain expertise across 20+ industry verticals, we
              collect and annotate data your models can rely on.
            </p>
            <div
              className="industry-metric"
              aria-label="Over 950 million assets annotated"
            >
              <strong>950M+</strong>
              <span>assets annotated</span>
            </div>
          </div>
        </div>

        <div className="industry-showcase">
          <div className="industry-list" aria-label="Industries we serve">
            {[industries.slice(0, 10), industries.slice(10)].map(
              (column, columnIndex) => (
                <div className="industry-list-column" key={columnIndex}>
                  {column.map((industry, itemIndex) => {
                    const index = columnIndex * 10 + itemIndex;
                    return (
                      <Link
                        className={`industry-list-item${
                          index === activeIndex ? " is-active" : ""
                        }`}
                        key={industry}
                        to={industryRoutes[industry]}
                        onMouseEnter={() => activate(index)}
                        onFocus={() => activate(index)}
                      >
                        <span className="industry-list-number">
                          {formatIndex(index)}
                        </span>

                        <span className="industry-list-name">{industry}</span>

                        <ArrowRight
                          className="industry-list-arrow"
                          aria-hidden="true"
                        />
                      </Link>
                    );
                  })}
                </div>
              ),
            )}
          </div>

          <div
            className="industry-feature-card"
            aria-live="off"
          >
            <Link
              className="industry-feature-link"
              to={industryRoutes[activeIndustry]}
              aria-label={`Explore ${activeIndustry}`}
            >
              {activeImage ? (
                <>
                  {previousImage && (
                    <img
                      key={`prev-${view.previous}`}
                      className="industry-feature-image industry-feature-image--prev"
                      src={previousImage}
                      alt=""
                      aria-hidden="true"
                    />
                  )}
                  <img
                    key={activeIndex}
                    className="industry-feature-image"
                    src={activeImage}
                    alt={activeIndustry}
                  />
                </>
              ) : (
                <div
                  className="industry-feature-placeholder"
                  aria-hidden="true"
                />
              )}

              <div className="industry-feature-shade" aria-hidden="true" />

              <div className="industry-feature-content">
                <div className="industry-feature-copy" key={activeIndex}>
                  

                  <h3 className="industry-feature-title">{activeName}</h3>

                  {activeDetail && (
                    <p className="industry-feature-detail">{activeDetail}</p>
                  )}
                </div>
              </div>
            </Link>

            <div
              className="industry-feature-controls"
              aria-label="Browse industries"
            >
              <button
                className="industry-control industry-control-previous"
                type="button"
                onClick={showPrevious}
                aria-label="Previous industry"
              >
                <ArrowLeft aria-hidden="true" />
              </button>

              <button
                className="industry-control industry-control-next"
                type="button"
                onClick={showNext}
                aria-label="Next industry"
              >
                <ArrowRight aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
